import React, { useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import styled from "styled-components";
import { Venue } from "sweet-global";
import CaretIcon from "sweet-web/components/icons/CaretIcon";
import CrossIcon from "sweet-web/components/icons/CrossIcon";
import { fadeIn } from "sweet-web/lib/animations";
import { colors } from "sweet-web/lib/styles";

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 51;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  margin: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  padding-top: 3px;
  border: none;
  background: transparent;
  outline: none;
  border-radius: 50px;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    transform: scale(1.05);
  }
  @media screen and (max-width: 600px) {
    width: 40px;
    width: 40px;
  }
`;

const BackButton = styled.button`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: absolute;
  bottom: 0;
  top: 0;
  left: -15px;
  margin-bottom: 5px;
  border: none;
  background: transparent;
  outline: none;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    transform: scale(1.05);
  }
  @media screen and (max-width: 600px) {
  }
`;

const NextButton = styled.button`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: absolute;
  bottom: 0;
  top: 0;
  right: -15px;
  margin-top: 5px;
  border: none;
  background: transparent;
  outline: none;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    transform: scale(1.05);
  }
  @media screen and (max-width: 600px) {
  }
`;

const Images = styled.div`
  margin: auto;
  align-self: center;
  width: 900px;
  height: 600px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  ${fadeIn}
  @media screen and (max-width: 900px) {
    width: 700px;
  }
  @media screen and (max-width: 600px) {
    width: 350px;
  }
`;

const Image = styled.img<{ active: boolean }>`
  max-height: 80%;
  max-width: 80%;
  object-fit: contain;
  border-radius: 5px;
  background-color: ${colors.white};
  display: ${props => (props.active ? "block" : "none")};
  ${fadeIn}
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: none;
  pointer-events: none;
  opacity: 0;
  @media screen and (max-width: 600px) {
    display: none;
  }
`;

type OwnProps = {
  photos: Venue["photos"];
  venueId: string;
};
type Props = OwnProps & RouteComponentProps;
const PhotoBox: React.FC<Props> = props => {
  const hiddenInputRef = useRef(null);

  const [activePhoto, setActivePhoto] = useState(0);

  const numberOfPhotos = Object.keys(props.photos).length;

  const leave = () => {
    props.history.replace(props.location.pathname.slice(0, -7));
  };

  const handleKey = e => {
    const key = e.key;
    switch (key) {
      case "ArrowRight":
        setActivePhoto((activePhoto + 1) % numberOfPhotos);
        break;
      case "ArrowLeft":
        setActivePhoto(Math.abs((activePhoto - 1) % numberOfPhotos));
        break;
      case "Escape":
        leave();
        break;
      default:
        break;
    }
  };

  const onClick =
    window.innerWidth < 600 ? () => {} : () => hiddenInputRef.current.focus();

  let prevPhoto = (activePhoto - 1) % numberOfPhotos;
  if (prevPhoto < 0) prevPhoto += numberOfPhotos;
  const nextPhoto = (activePhoto + 1) % numberOfPhotos;

  return (
    <Wrapper onClick={onClick}>
      <Images>
        <BackButton onClick={() => setActivePhoto(prevPhoto)}>
          <CaretIcon
            color={colors.gray}
            size={20}
            style={{ transform: "rotate(180deg)" }}
          />
        </BackButton>
        {Object.values(props.photos).map((photo, i) => (
          <Image
            key={`photo-box-image-${i}`}
            src={photo.file.url}
            active={activePhoto === i}
          />
        ))}

        <NextButton onClick={() => setActivePhoto(nextPhoto)}>
          <CaretIcon color={colors.gray} size={20} />
        </NextButton>
      </Images>
      <HiddenInput
        ref={hiddenInputRef}
        type="text"
        autoFocus={true}
        onKeyDown={handleKey}
      />
      <CloseButton onClick={leave}>
        <CrossIcon color={colors.gray} size={24} />
      </CloseButton>
    </Wrapper>
  );
};

export default withRouter(PhotoBox);
